/**
 * Supabase persistence for the local-articles store.
 *
 * Maps KBStore onto scms_kb + scms_kb_category / _section / _article.
 * Only changed rows are written -- see ./diff.ts.
 */

import { cmsDb } from "./client"
import { sanitizeHtml } from "@/lib/sanitizeHtml"
import type { KBStore, CategoryNode, SectionNode, ArticleNode } from "../../articles/types"
import { emptyStore } from "../../articles/types"
import type { StoreAdapter } from "./types"
import { diffRows, isEmptyDiff, getSnapshot, putSnapshot } from "./diff"

const KB = "scms_kb"
const CATEGORY = "scms_kb_category"
const SECTION = "scms_kb_section"
const ARTICLE = "scms_kb_article"

const categoryToRow = (n: CategoryNode, kbId: string, teamId: string) => ({
  id: n.id,
  kb_id: kbId,
  team_id: teamId,
  position: n.position,
  source_locale: n.sourceLocale,
  translations: n.translations ?? {},
  icon: (n as any).icon ?? null,
  created_at_ms: n.createdAt,
  updated_at_ms: n.updatedAt,
})

const rowToCategory = (r: any): CategoryNode => ({
  id: r.id,
  kind: "category",
  position: r.position ?? 0,
  sourceLocale: r.source_locale,
  translations: r.translations ?? {},
  ...(r.icon ? { icon: r.icon } : {}),
  createdAt: Number(r.created_at_ms),
  updatedAt: Number(r.updated_at_ms),
}) as CategoryNode

const sectionToRow = (n: SectionNode, kbId: string, teamId: string) => ({
  id: n.id,
  kb_id: kbId,
  team_id: teamId,
  category_id: n.categoryId,
  parent_section_id: n.parentSectionId ?? null,
  position: n.position,
  source_locale: n.sourceLocale,
  translations: n.translations ?? {},
  icon: (n as any).icon ?? null,
  created_at_ms: n.createdAt,
  updated_at_ms: n.updatedAt,
})

const rowToSection = (r: any): SectionNode => ({
  id: r.id,
  kind: "section",
  categoryId: r.category_id,
  // null (not undefined) means "top level of the category" -- a meaningful
  // value in this model, so it is preserved rather than dropped.
  parentSectionId: r.parent_section_id ?? null,
  position: r.position ?? 0,
  sourceLocale: r.source_locale,
  translations: r.translations ?? {},
  ...(r.icon ? { icon: r.icon } : {}),
  createdAt: Number(r.created_at_ms),
  updatedAt: Number(r.updated_at_ms),
}) as SectionNode

// Article bodies are HTML; scripts and event handlers never reach the database from here
const cleanBodies = (translations: ArticleNode["translations"]) =>
  Object.fromEntries(Object.entries(translations).map(([locale, t]) =>
    [locale, t && typeof t.body === "string" ? { ...t, body: sanitizeHtml(t.body) } : t]))

const articleToRow = (n: ArticleNode, kbId: string, teamId: string) => ({
  id: n.id,
  kb_id: kbId,
  team_id: teamId,
  section_id: n.sectionId,
  position: n.position,
  source_locale: n.sourceLocale,
  translations: cleanBodies(n.translations ?? {}),
  // Deprecated whole-article state; per-locale state lives in translations.
  state: n.state ?? "draft",
  labels: n.labels ?? [],
  attachments: n.attachments ?? [],
  source_ref: n.sourceRef ?? null,
  created_at_ms: n.createdAt,
  updated_at_ms: n.updatedAt,
})

const rowToArticle = (r: any): ArticleNode => ({
  id: r.id,
  kind: "article",
  sectionId: r.section_id,
  position: r.position ?? 0,
  sourceLocale: r.source_locale,
  translations: r.translations ?? {},
  state: r.state ?? "draft",
  labels: r.labels ?? [],
  attachments: r.attachments ?? [],
  sourceRef: r.source_ref ?? undefined,
  createdAt: Number(r.created_at_ms),
  updatedAt: Number(r.updated_at_ms),
}) as ArticleNode

export function makeArticlesAdapter(teamId: string): StoreAdapter<KBStore> {
  const snapKey = (table: string, dbId: string) => `${teamId}:${dbId}:${table}`

  return {
    async load(kbId) {
      const [kbRes, catRes, secRes, artRes] = await Promise.all([
        cmsDb.from(KB).select("*").eq("id", kbId),
        cmsDb.from(CATEGORY).select("*").eq("kb_id", kbId),
        cmsDb.from(SECTION).select("*").eq("kb_id", kbId),
        cmsDb.from(ARTICLE).select("*").eq("kb_id", kbId),
      ])
      for (const r of [kbRes, catRes, secRes, artRes]) if (r.error) throw r.error
      // No KB row means this team has not used the DB backend yet.
      if (!kbRes.data?.length) return null

      const meta = kbRes.data[0]
      const store = emptyStore(meta.default_locale ?? "en-US")
      store.locales = meta.locales ?? [store.defaultLocale]

      const catRows: Record<string, any> = {}
      const secRows: Record<string, any> = {}
      const artRows: Record<string, any> = {}

      for (const r of catRes.data ?? []) {
        store.categories[r.id] = rowToCategory(r)
        catRows[r.id] = categoryToRow(store.categories[r.id], kbId, teamId)
      }
      for (const r of secRes.data ?? []) {
        store.sections[r.id] = rowToSection(r)
        secRows[r.id] = sectionToRow(store.sections[r.id], kbId, teamId)
      }
      for (const r of artRes.data ?? []) {
        store.articles[r.id] = rowToArticle(r)
        artRows[r.id] = articleToRow(store.articles[r.id], kbId, teamId)
      }

      putSnapshot(snapKey(CATEGORY, kbId), catRows)
      putSnapshot(snapKey(SECTION, kbId), secRows)
      putSnapshot(snapKey(ARTICLE, kbId), artRows)
      return store
    },

    async save(kbId, store) {
      // The KB row carries the locale list, so it is upserted every save --
      // it is a single row, and diffing it would cost more than writing it.
      const { error: kbErr } = await cmsDb.from(KB).upsert({
        id: kbId,
        team_id: teamId,
        default_locale: store.defaultLocale,
        locales: store.locales ?? [],
      })
      if (kbErr) throw kbErr

      const catRows: Record<string, any> = {}
      for (const n of Object.values(store.categories)) catRows[n.id] = categoryToRow(n, kbId, teamId)
      const secRows: Record<string, any> = {}
      for (const n of Object.values(store.sections)) secRows[n.id] = sectionToRow(n, kbId, teamId)
      const artRows: Record<string, any> = {}
      for (const n of Object.values(store.articles)) artRows[n.id] = articleToRow(n, kbId, teamId)

      const catDiff = diffRows(getSnapshot(snapKey(CATEGORY, kbId)), catRows)
      const secDiff = diffRows(getSnapshot(snapKey(SECTION, kbId)), secRows)
      const artDiff = diffRows(getSnapshot(snapKey(ARTICLE, kbId)), artRows)

      /*
       * Write order follows the tree: a section's category and an article's
       * section must exist before the child row references them. Deletes run
       * in the reverse order, leaves first, so nothing is removed while it is
       * still referenced.
       */
      const up = async (table: string, rows: any[]) => {
        if (!rows.length) return
        const { error } = await cmsDb.from(table).upsert(rows)
        if (error) throw error
      }
      await up(CATEGORY, [...catDiff.inserted, ...catDiff.updated])
      await up(SECTION, [...secDiff.inserted, ...secDiff.updated])
      await up(ARTICLE, [...artDiff.inserted, ...artDiff.updated])

      const del = async (table: string, ids: string[]) => {
        if (!ids.length) return
        const { error } = await cmsDb.from(table).delete().in("id", ids)
        if (error) throw error
      }
      await del(ARTICLE, artDiff.deletedIds)
      await del(SECTION, secDiff.deletedIds)
      await del(CATEGORY, catDiff.deletedIds)

      if (!isEmptyDiff(catDiff)) putSnapshot(snapKey(CATEGORY, kbId), catRows)
      if (!isEmptyDiff(secDiff)) putSnapshot(snapKey(SECTION, kbId), secRows)
      if (!isEmptyDiff(artDiff)) putSnapshot(snapKey(ARTICLE, kbId), artRows)
    },

    async clear(kbId) {
      // Deleting the KB row cascades to categories, sections and articles.
      const { error } = await cmsDb.from(KB).delete().eq("id", kbId)
      if (error) throw error
      putSnapshot(snapKey(CATEGORY, kbId), {})
      putSnapshot(snapKey(SECTION, kbId), {})
      putSnapshot(snapKey(ARTICLE, kbId), {})
    },
  }
}
